import React, { useEffect, useState, useCallback } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import SectionEditModal from '../profile/SectionEditModal.jsx';
import { uploadBrandAsset } from '../../lib/uploadBrandAsset.js';

const HEADLINE_MAX = 120;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_MB = 2 * 1024 * 1024;

function normalizeWebsite(input) {
  const s = (input || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
}

function isValidUrl(s) {
  try {
    new URL(s);
    return true;
  } catch {
    return false;
  }
}

/**
 * Modal: edit brand identity card only (brandProfiles/{uid} contact, company name/website/role, media.personAvatar).
 * Company logo is edited separately (work experience modal). Existing company.logoUrl is preserved on save.
 */
function BrandIdentityEditModal({ isOpen, onClose, userId, brandProfile, onSaved }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [linkedInUrl, setLinkedInUrl] = useState('');
  const [headline, setHeadline] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [roleTitle, setRoleTitle] = useState('');

  const [personAvatarUrl, setPersonAvatarUrl] = useState('');
  const [personFile, setPersonFile] = useState(null);
  const [personPreview, setPersonPreview] = useState('');
  const [personUploading, setPersonUploading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const hydrateFromProfile = useCallback(() => {
    const c = brandProfile?.contact || {};
    const co = brandProfile?.company || {};
    const m = brandProfile?.media || {};
    setFirstName(c.firstName || '');
    setLastName(c.lastName || '');
    setEmail(c.email || '');
    setLinkedInUrl(c.linkedInUrl || '');
    setHeadline(c.headline || '');
    setCompanyName(co.name || '');
    setCompanyWebsite(co.website || '');
    setRoleTitle(co.roleTitle || '');
    setPersonAvatarUrl(m.personAvatarUrl || '');
    setPersonPreview(m.personAvatarUrl || '');
    setPersonFile(null);
    setErrors({});
  }, [brandProfile]);

  useEffect(() => {
    if (isOpen) hydrateFromProfile();
  }, [isOpen, hydrateFromProfile]);

  const onPersonFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErrors((prev) => ({ ...prev, _general: undefined }));
    if (!ALLOWED_TYPES.includes(file.type)) {
      setErrors({ _general: 'Photo must be PNG, JPEG, or WebP.' });
      return;
    }
    if (file.size > MAX_MB) {
      setErrors({ _general: 'Photo must be 2MB or less.' });
      return;
    }
    setPersonFile(file);
    setPersonPreview(URL.createObjectURL(file));
    setPersonUploading(true);
    try {
      const url = await uploadBrandAsset(file, userId, 'person-avatar');
      setPersonAvatarUrl(url);
    } catch (err) {
      setErrors({ _general: err.message || 'Photo upload failed.' });
      setPersonPreview(personAvatarUrl || '');
      setPersonFile(null);
    } finally {
      setPersonUploading(false);
    }
  };

  const validate = () => {
    const next = {};
    if (!firstName.trim()) next.firstName = 'First name is required.';
    if (!lastName.trim()) next.lastName = 'Last name is required.';
    if (!email.trim()) next.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = 'Enter a valid email.';
    if (linkedInUrl.trim() && !isValidUrl(linkedInUrl.trim())) next.linkedInUrl = 'Enter a valid URL.';
    if (!companyName.trim()) next.companyName = 'Company name is required.';
    const w = normalizeWebsite(companyWebsite);
    if (!w) next.companyWebsite = 'Company website is required.';
    else if (!isValidUrl(w)) next.companyWebsite = 'Enter a valid website URL.';
    if (!roleTitle.trim()) next.roleTitle = 'Role / title is required.';
    if (!personAvatarUrl?.trim() && !personFile) next.personPhoto = 'Profile photo is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!userId || !validate()) return;
    if (personUploading) return;

    setSaving(true);
    setErrors({});
    try {
      let avatarUrl = (personAvatarUrl || '').trim();
      if (!avatarUrl && personFile) {
        avatarUrl = await uploadBrandAsset(personFile, userId, 'person-avatar');
      }
      if (!avatarUrl) {
        setErrors({ _general: 'Profile photo is required.' });
        setSaving(false);
        return;
      }

      const websiteNorm = normalizeWebsite(companyWebsite);
      const prevContact = brandProfile?.contact || {};
      const prevCompany = brandProfile?.company || {};

      const nextContact = {
        ...prevContact,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        headline: headline.trim().slice(0, HEADLINE_MAX) || null,
      };
      if (linkedInUrl.trim()) nextContact.linkedInUrl = linkedInUrl.trim();
      else delete nextContact.linkedInUrl;

      const nextCompany = {
        ...prevCompany,
        name: companyName.trim(),
        website: websiteNorm,
        roleTitle: roleTitle.trim(),
      };

      const nextMedia = {
        ...(brandProfile?.media || {}),
        personAvatarUrl: avatarUrl,
      };

      const ref = doc(db, 'brandProfiles', userId);
      await setDoc(
        ref,
        {
          contact: nextContact,
          company: nextCompany,
          media: nextMedia,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      const merged = {
        ...brandProfile,
        contact: nextContact,
        company: nextCompany,
        media: nextMedia,
      };
      onSaved(merged);
      onClose();
    } catch (err) {
      console.error('[BrandIdentityEditModal]', err);
      setErrors({ _general: err.message || 'Could not save. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionEditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit profile"
      subtitle="Update how you appear to creators on your profile card."
      onSave={handleSave}
      isSaving={saving || personUploading}
      errors={errors}
      saveLabel="Save changes"
      disableSave={personUploading}
    >
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700">Profile photo</label>
          <div className="mt-2 flex items-center gap-4">
            <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-full border-2 border-slate-200 bg-slate-100">
              {personPreview ? (
                <img src={personPreview} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-slate-400 text-xs">Photo</div>
              )}
            </div>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onPersonFile}
              disabled={personUploading || saving}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200 disabled:opacity-50"
            />
          </div>
          {personUploading && <p className="mt-1 text-xs text-slate-500">Uploading photo…</p>}
          {errors.personPhoto && <p className="mt-1 text-xs text-red-600">{errors.personPhoto}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">First name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            />
            {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Last name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            />
            {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">Work email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
          />
          {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">Headline</label>
          <input
            type="text"
            value={headline}
            onChange={(e) => setHeadline(e.target.value.slice(0, HEADLINE_MAX))}
            placeholder="What you are looking for (e.g. UGC, ambassadors)"
            maxLength={HEADLINE_MAX}
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
          />
          <p className="mt-1 text-xs text-slate-500">{headline.length}/{HEADLINE_MAX}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">LinkedIn (optional)</label>
          <input
            type="url"
            value={linkedInUrl}
            onChange={(e) => setLinkedInUrl(e.target.value)}
            placeholder="https://linkedin.com/in/..."
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
          />
          {errors.linkedInUrl && <p className="mt-1 text-xs text-red-600">{errors.linkedInUrl}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700">Company name</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            />
            {errors.companyName && <p className="mt-1 text-xs text-red-600">{errors.companyName}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Your role / title</label>
            <input
              type="text"
              value={roleTitle}
              onChange={(e) => setRoleTitle(e.target.value)}
              placeholder="e.g. CEO, Marketing Lead"
              className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            />
            {errors.roleTitle && <p className="mt-1 text-xs text-red-600">{errors.roleTitle}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">Company website</label>
          <input
            type="text"
            value={companyWebsite}
            onChange={(e) => setCompanyWebsite(e.target.value)}
            placeholder="example.com or https://example.com"
            className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
          />
          {errors.companyWebsite && <p className="mt-1 text-xs text-red-600">{errors.companyWebsite}</p>}
        </div>
      </div>
    </SectionEditModal>
  );
}

export default BrandIdentityEditModal;
